/**
 * Geocoding API - OpenStreetMap Nominatim for global search
 * Free, no API key required
 */

import type { SearchResult } from '@/stores/useNavigationStore';

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';

interface NominatimResult {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  class: string;
  type: string;
  place_rank: number;
  importance: number;
  addresstype: string;
  name: string;
  display_name: string;
  address?: {
    road?: string;
    suburb?: string;
    city?: string;
    state?: string;
    country?: string;
    postcode?: string;
  };
  boundingbox: string[];
}

/**
 * Search for places globally using OpenStreetMap Nominatim
 */
export async function searchPlaces(
  query: string,
  options?: {
    limit?: number;
    userLocation?: { lat: number; lng: number };
    countrycodes?: string; // Comma-separated country codes (e.g., 'us,gb,my')
  }
): Promise<SearchResult[]> {
  if (!query.trim()) return [];

  const params = new URLSearchParams({
    q: query,
    format: 'json',
    addressdetails: '1',
    limit: String(options?.limit || 10),
  });

  // Bias results toward user's location if available
  if (options?.userLocation) {
    params.set('viewbox', getBoundingBox(options.userLocation, 50)); // 50km radius
    params.set('bounded', '0'); // Don't strictly bound, just bias
  }

  // Optionally filter by country
  if (options?.countrycodes) {
    params.set('countrycodes', options.countrycodes);
  }

  try {
    const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params}`, {
      headers: {
        'User-Agent': 'Blurp.ie/1.0 (Cognitive Navigation App)',
      },
    });

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const results: NominatimResult[] = await response.json();
    return results.map((result) => nominatimToSearchResult(result, options?.userLocation));
  } catch (error) {
    console.error('Geocoding search failed:', error);
    return [];
  }
}

/**
 * Reverse geocode coordinates to address
 */
export async function reverseGeocode(
  lat: number,
  lng: number
): Promise<SearchResult | null> {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lng),
    format: 'json',
    addressdetails: '1',
  });

  try {
    const response = await fetch(`${NOMINATIM_BASE_URL}/reverse?${params}`, {
      headers: {
        'User-Agent': 'Blurp.ie/1.0 (Cognitive Navigation App)',
      },
    });

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const result: NominatimResult = await response.json();
    return nominatimToSearchResult(result);
  } catch (error) {
    console.error('Reverse geocoding failed:', error);
    return null;
  }
}

/**
 * Convert Nominatim result to our SearchResult format
 */
function nominatimToSearchResult(
  result: NominatimResult,
  userLocation?: { lat: number; lng: number }
): SearchResult {
  const lat = parseFloat(result.lat);
  const lng = parseFloat(result.lon);

  // Calculate distance from user if location available
  let distance: number | undefined;
  if (userLocation) {
    distance = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
  }

  // Determine category based on OSM class/type
  const category = mapOsmTypeToCategory(result.class, result.type);

  // Format address
  const address = formatAddress(result);

  return {
    id: `osm_${result.osm_type}_${result.osm_id}`,
    name: result.name || result.display_name.split(',')[0],
    address,
    coordinates: { lat, lng },
    distance,
    category,
  };
}

/**
 * Format address from Nominatim result
 */
function formatAddress(result: NominatimResult): string {
  if (!result.address) {
    return result.display_name;
  }

  const parts = [];
  const addr = result.address;

  if (addr.road) parts.push(addr.road);
  if (addr.suburb) parts.push(addr.suburb);
  if (addr.city) parts.push(addr.city);
  if (addr.state) parts.push(addr.state);
  if (addr.country) parts.push(addr.country);

  return parts.length > 0 ? parts.join(', ') : result.display_name;
}

/**
 * Map OSM class/type to our category format
 */
function mapOsmTypeToCategory(osmClass: string, osmType: string): string | undefined {
  const categoryMap: Record<string, string> = {
    // Leisure
    'leisure:park': 'Park',
    'leisure:garden': 'Garden',
    'leisure:playground': 'Playground',
    // Amenity
    'amenity:cafe': 'Cafe',
    'amenity:restaurant': 'Restaurant',
    'amenity:hospital': 'Hospital',
    'amenity:pharmacy': 'Pharmacy',
    'amenity:school': 'School',
    'amenity:university': 'University',
    'amenity:library': 'Library',
    'amenity:bank': 'Bank',
    // Shop
    'shop:supermarket': 'Supermarket',
    'shop:mall': 'Mall',
    'shop:convenience': 'Store',
    // Tourism
    'tourism:hotel': 'Hotel',
    'tourism:museum': 'Museum',
    'tourism:attraction': 'Attraction',
    // Transport
    'aeroway:aerodrome': 'Airport',
    'railway:station': 'Station',
    'amenity:bus_station': 'Bus Station',
    // Place types
    'place:city': 'City',
    'place:town': 'Town',
    'place:village': 'Village',
    'place:suburb': 'Area',
    // Building
    'building:office': 'Office',
    'building:residential': 'Residential',
  };

  const key = `${osmClass}:${osmType}`;
  return categoryMap[key] || undefined;
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Get bounding box string for Nominatim viewbox parameter
 */
function getBoundingBox(
  center: { lat: number; lng: number },
  radiusKm: number
): string {
  // Approximate degrees per km at given latitude
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos(toRad(center.lat)));

  const minLon = center.lng - lngDelta;
  const maxLon = center.lng + lngDelta;
  const minLat = center.lat - latDelta;
  const maxLat = center.lat + latDelta;

  return `${minLon},${maxLat},${maxLon},${minLat}`;
}

export default { searchPlaces, reverseGeocode };
